import cx from 'classnames'
import { NavLink, Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { SidebarNavigationSteps } from '@/shared/components'
import { ProductionRouteCreationState } from '@/features/production-routes/api/ProductionRouteModel'
import { H2 } from '@/common'
import { compact } from 'es-toolkit'
import { ChevronLeftIcon } from '@/common/components/icons'

function BackButton({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={cx(
        'flex items-center gap-2 py-3.5 px-2 text-sm rounded-xs text-green-600 font-medium bg-green-300',
        className
      )}
    >
      <ChevronLeftIcon className="size-4" />
      {children}
    </span>
  )
}

export default function ProductionRouteLayout({
  state: productionRoute,
  isUsingEmissionPoints
}: {
  state: ProductionRouteCreationState
  isUsingEmissionPoints: boolean
}) {
  const { t } = useTranslation()

  const {
    productionMethods,
    aggregatedGood,
    assets,
    heatFlowSystem,
    electricityProductionMethods,
    isUsingWasteGas,
    storedCO2Handling
  } = productionRoute

  const isDefinitionComplete = aggregatedGood !== '' && productionMethods.length > 0
  const isEmissionPointsComplete = assets.length > 0
  const isHeatComplete = heatFlowSystem !== null
  const isElectricityComplete = electricityProductionMethods.length > 0
  const isWasteGasComplete = isUsingWasteGas !== null
  const isStoredCO2Complete = storedCO2Handling !== null
  const steps = compact([
    {
      title: t('productionRoute:steps.goodsAndProductionMethods'),
      isComplete: isDefinitionComplete,
      link: 'definition'
    },
    isUsingEmissionPoints
      ? {
          title: t('productionRoute:steps.emissionPoints'),
          isComplete: isEmissionPointsComplete,
          link: 'emission-points'
        }
      : null,
    {
      title: t('productionRoute:steps.heatFlows'),
      isComplete: isHeatComplete,
      link: 'heat'
    },
    {
      title: t('productionRoute:steps.electricity'),
      isComplete: isElectricityComplete,
      link: 'electricity'
    },
    {
      title: t('productionRoute:steps.wasteGas'),
      isComplete: isWasteGasComplete,
      link: 'waste-gas'
    },
    {
      title: t('productionRoute:steps.storedCO2'),
      isComplete: isStoredCO2Complete,
      link: 'stored-co2'
    },
    {
      title: t('productionRoute:steps.summary'),
      isComplete: false,
      link: 'summary'
    }
  ])

  return (
    <div className="lg:max-w-full">
      <aside
        className="fixed shadow-sidebar top-0 pt-16 w-60 bg-white h-screen transition-transform -translate-x-full sm:translate-x-0"
        aria-label="Sidebar"
      >
        <div className="p-4">
          <NavLink to="/dashboard" className="mb-14 block">
            <BackButton>{t('common:actions.backToDashboard')}</BackButton>
          </NavLink>
          <SidebarNavigationSteps steps={steps} />
        </div>
      </aside>
      <div className="sm:ml-60 p-4 transition-all duration-300 flex flex-col h-full">
        <header className="mb-6">
          <H2>{productionRoute.name}</H2>
        </header>
        <Outlet />
      </div>
    </div>
  )
}
